import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    GlobalAccount,
    GUID,
    OpportunityCommentRequest,
    OpportunityRequest,
    OpportunityStatus,
    OpportunityTimelineEntryView,
    OpportunityView,
    Tenant,
} from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { useSelectedTenantGUIDs } from 'views/auth/tenant-selection';

import { SuggestedOpportunityQueryKeys } from './suggested-opportunity-queries';

const OpportunityQueryKeys = {
    ALL_OPPORTUNITIES: ['opportunities'],
    opportunitiesByTenants: (tenantGUIDs: GUID<Tenant>[]) => ['opportunities', 'tenants', tenantGUIDs],
    opportunitiesByHousehold: (householdGUID: GUID<GlobalAccount>) => ['opportunities', 'household', householdGUID],
    opportunity: (opportunityGUID: GUID<OpportunityView>) => ['opportunities', opportunityGUID],
    opportunityTimeline: (opportunityGUID: GUID<OpportunityView>) => ['opportunities', opportunityGUID, 'timeline'],
} as const;

export const useListOpportunities = () => {
    const tenantGUIDs = useSelectedTenantGUIDs();

    return useQuery({
        queryKey: OpportunityQueryKeys.opportunitiesByTenants(tenantGUIDs),
        enabled: tenantGUIDs.length > 0,
        queryFn: async () => {
            const [opportunities] = await CustomerApi().listOpportunitiesByTenants(tenantGUIDs);
            return opportunities ?? [];
        },
    });
};

export const useListOpportunitiesByHousehold = (householdGUID: GUID<GlobalAccount>) => {
    return useQuery({
        queryKey: OpportunityQueryKeys.opportunitiesByHousehold(householdGUID),
        enabled: !!householdGUID,
        queryFn: async () => {
            const [opportunities] = await CustomerApi().listOpportunitiesByHousehold(householdGUID);
            return opportunities ?? [];
        },
    });
};

export const useOpportunity = (opportunityGUID: GUID<OpportunityView>) => {
    return useQuery({
        queryKey: OpportunityQueryKeys.opportunity(opportunityGUID),
        enabled: !!opportunityGUID,
        queryFn: async () => {
            const [opportunity] = await CustomerApi().getOpportunity(opportunityGUID);
            return opportunity ?? null;
        },
    });
};

export const useCreateOpportunity = () => {
    const queryClient = useQueryClient();
    return useMutation({
        // noteGUID is not sent; it only names the note whose suggestions to refetch after a
        // create that converts one of them.
        mutationFn: async (data: {
            householdGUID: GUID<GlobalAccount>;
            request: OpportunityRequest;
            noteGUID?: string;
        }) => {
            const [opportunityGUID] = await CustomerApi().createOpportunity(data.householdGUID, data.request);
            return opportunityGUID;
        },
        onSuccess: (_opportunityGUID, variables) => {
            if (variables.noteGUID) {
                queryClient.invalidateQueries({
                    queryKey: SuggestedOpportunityQueryKeys.byNote(variables.noteGUID),
                });
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: OpportunityQueryKeys.ALL_OPPORTUNITIES,
            });
        },
    });
};

export const useUpdateOpportunity = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: { opportunityGUID: GUID<OpportunityView>; request: OpportunityRequest }) => {
            await CustomerApi().updateOpportunity(data.opportunityGUID, data.request);
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: OpportunityQueryKeys.ALL_OPPORTUNITIES,
            });
        },
    });
};

export const useDeleteOpportunity = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (opportunityGUID: GUID<OpportunityView>) => {
            await CustomerApi().deleteOpportunity(opportunityGUID);
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: OpportunityQueryKeys.ALL_OPPORTUNITIES,
            });
        },
    });
};

export const useOpportunityTimeline = (opportunityGuid: GUID<OpportunityView>) =>
    useQuery<OpportunityTimelineEntryView[]>({
        enabled: !!opportunityGuid,
        queryKey: OpportunityQueryKeys.opportunityTimeline(opportunityGuid),
        queryFn: async () => {
            const [timeline] = await CustomerApi().listOpportunityTimeline(opportunityGuid);
            return timeline ?? [];
        },
    });

export const useCreateOpportunityComment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            opportunityGuid,
            request,
        }: {
            opportunityGuid: GUID<OpportunityView>;
            request: OpportunityCommentRequest;
        }) => {
            await CustomerApi().createOpportunityComment(opportunityGuid, request);
        },
        onSettled: (_data, _error, variables) => {
            queryClient.invalidateQueries({
                queryKey: OpportunityQueryKeys.opportunityTimeline(variables.opportunityGuid),
            });
        },
    });
};

export const useUpdateOpportunityComment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            opportunityGuid,
            commentGUID,
            request,
        }: {
            opportunityGuid: GUID<OpportunityView>;
            commentGUID: string;
            request: OpportunityCommentRequest;
        }) => {
            await CustomerApi().updateOpportunityComment(opportunityGuid, commentGUID, request);
        },
        onSettled: (_data, _error, variables) => {
            queryClient.invalidateQueries({
                queryKey: OpportunityQueryKeys.opportunityTimeline(variables.opportunityGuid),
            });
        },
    });
};

export const useDeleteOpportunityComment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            opportunityGuid,
            commentGUID,
        }: {
            opportunityGuid: GUID<OpportunityView>;
            commentGUID: string;
        }) => {
            await CustomerApi().deleteOpportunityComment(opportunityGuid, commentGUID);
        },
        onSettled: (_data, _error, variables) => {
            queryClient.invalidateQueries({
                queryKey: OpportunityQueryKeys.opportunityTimeline(variables.opportunityGuid),
            });
        },
    });
};

export const useUpdateOpportunityStatus = () => {
    const queryClient = useQueryClient();
    const tenantGUIDs = useSelectedTenantGUIDs();

    const mutateOpportunity = (opportunity: OpportunityView) => {
        queryClient.setQueryData(
            OpportunityQueryKeys.opportunitiesByTenants(tenantGUIDs),
            (data: OpportunityView[] = []) =>
                data.map((item) =>
                    item.opportunityGUID === opportunity.opportunityGUID
                        ? { ...opportunity, updatedAt: new Date().toISOString() }
                        : item,
                ),
        );
    };

    return useMutation({
        mutationFn: async (data: { opportunity: OpportunityView; status: OpportunityStatus }) => {
            // optimistic update
            mutateOpportunity({
                ...data.opportunity,
                status: data.status,
            });
            await CustomerApi().updateOpportunity(data.opportunity.opportunityGUID, {
                name: data.opportunity.name,
                description: data.opportunity.description,
                status: data.status,
                value: data.opportunity.value.toString(),
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: OpportunityQueryKeys.ALL_OPPORTUNITIES,
            });
        },
    });
};
